import { MetaFunction, useLoaderData, useSubmit } from '@remix-run/react';
import { DataFunctionArgs, json } from '@remix-run/server-runtime';
import { withZod } from '@remix-validated-form/with-zod';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidatedForm } from 'remix-validated-form';
import { Breadcrumbs } from '~/components/Breadcrumbs';
import { CategoryFilterBar } from '~/components/collections/CategoryFilterBar';
import { SubCategoryIcons } from '~/components/collections/SubCategoryIcons';
import { FacetFilterTracker } from '~/components/facet-filter/facet-filter-tracker';
import { Sidebar } from '~/components/home/Sidebar';
import { InfiniteProductGrid } from '~/components/products/InfiniteProductGrid';
import { APP_META_TITLE } from '~/constants';
import type { Collection, FacetValueResult } from '~/generated/graphql';
import { getCollections } from '~/providers/collections/collections';
import { filteredSearchLoaderFromPagination } from '~/utils/filtered-search-loader';
import { sdk } from '../graphqlWrapper';

export async function loader({ params, request, context }: DataFunctionArgs) {
  // Parallel fetch for better performance
  const [searchResult, collectionResult, allCollections] = await Promise.all([
    filteredSearchLoader({ params, request, context }),
    sdk.collection({ slug: params.slug }),
    getCollections(request),
  ]);

  const {
    result,
    resultWithoutFacetValueFilters,
    facetValueIds,
    appliedPaginationLimit,
    appliedPaginationPage,
    term,
  } = searchResult;

  const collection = collectionResult.collection;
  if (!collection?.id || !collection?.name) {
    throw new Response('Not Found', {
      status: 404,
    });
  }

  return json(
    {
      term,
      collection,
      allCollections: allCollections || [],
      result,
      resultWithoutFacetValueFilters,
      facetValueIds,
      appliedPaginationLimit,
      appliedPaginationPage,
    },
    {
      headers: {
        'Cache-Control': 'public, max-age=60, stale-while-revalidate=300',
      },
    },
  );
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const collection = data?.collection;
  const title = collection
    ? `${collection.name} - ${APP_META_TITLE}`
    : APP_META_TITLE;

  const description = collection?.description
    ? collection.description.replace(/<[^>]*>/g, '').slice(0, 160)
    : `Browse ${collection?.name || 'products'} on RedTab. Find quality items at great prices.`;

  return [
    { title },
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: 'website' },
    ...(collection?.featuredAsset?.preview
      ? [{ property: 'og:image', content: collection.featuredAsset.preview }]
      : []),
  ];
};

const paginationLimitMinimumDefault = 24;
const allowedPaginationLimits = new Set<number>([
  paginationLimitMinimumDefault,
  48,
  72,
]);
const { validator, filteredSearchLoader } = filteredSearchLoaderFromPagination(
  allowedPaginationLimits,
  paginationLimitMinimumDefault,
);

export default function CollectionSlug() {
  const loaderData = useLoaderData<typeof loader>();
  const {
    collection,
    allCollections,
    result,
    resultWithoutFacetValueFilters,
    facetValueIds,
  } = loaderData;
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const facetValuesTracker = useRef(new FacetFilterTracker());
  facetValuesTracker.current.update(
    result,
    resultWithoutFacetValueFilters,
    facetValueIds,
  );
  const submit = useSubmit();
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Get parent collection to find sibling categories for icons
  const parentCollection = allCollections.find(
    (c) => c.children?.some((child) => child.slug === collection.slug)
  );
  const siblingCategories = parentCollection?.children || collection.children || [];

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="flex gap-6">
          {/* Left Sidebar - Hidden on mobile */}
          <Sidebar
            collections={allCollections as Collection[]}
            activeSlug={collection.slug}
          />

          {/* Main Content Area */}
          <div className="flex-1 min-w-0 space-y-4">
            {/* Header Card - Breadcrumbs, Title, Category Icons */}
            <div className="bg-white rounded-3xl p-6 border border-gray-100">
              {/* Breadcrumbs */}
              <Breadcrumbs items={collection.breadcrumbs} />

              {/* Title */}
              <h1
                className="font-semibold text-black mt-2 mb-4"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '28px',
                  lineHeight: '36px',
                  letterSpacing: '-0.04em',
                }}
              >
                {collection.name}
              </h1>

              {/* Category Icons Row */}
              {siblingCategories.length > 0 && (
                <SubCategoryIcons
                  categories={siblingCategories}
                  activeSlug={collection.slug}
                />
              )}
            </div>

            {/* Filter & Products Card */}
            <div className="bg-white rounded-3xl p-6 border border-gray-100">
              {/* Filter Bar */}
              <CategoryFilterBar
                orderedCount={5}
                facetValues={result?.facetValues as FacetValueResult[] | undefined}
                activeFacetValueIds={facetValueIds}
                viewMode={viewMode}
                setViewMode={setViewMode}
              />

              {/* Product grid with infinite scroll */}
              <InfiniteProductGrid
                mobileFiltersOpen={mobileFiltersOpen}
                setMobileFiltersOpen={setMobileFiltersOpen}
                collectionSlug={collection.slug}
                hideFilters={true}
                viewMode={viewMode}
                {...loaderData}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CatchBoundary() {
  const { t } = useTranslation();

  return (
    <div className="container mx-auto px-4">
      <h2 className="text-3xl sm:text-5xl font-light tracking-tight text-gray-900 my-8">
        {t('product.collectionNotFound')}
      </h2>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-6 gap-4">
        {/* Sidebar skeleton */}
        <div className="space-y-6">
          <div className="h-2 bg-slate-200 rounded"></div>
          <div className="h-2 bg-slate-200 rounded"></div>
          <div className="h-2 bg-slate-200 rounded"></div>
        </div>

        {/* Product cards skeleton */}
        <div className="sm:col-span-5 lg:col-span-4">
          <div className="grid grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            <div className="h-64 bg-slate-200 rounded"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>
    </div>
  );
}
